import { Button, Page, PageTitleWithButton } from '../../../components';
import './styles.scss';
import 'moment-timezone';
import { ClosingCalendar } from './closing-calendar';
import { Box, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { IGetCalendarEvent } from '../../../models/calendar';
import moment from 'moment';
import { useQuery } from 'react-query';
import { Views } from 'react-big-calendar';
import { getClosingCalendarEvents, updateClosingCalendarEvents } from '../../../fetch/calendar';
import { deepEqual } from 'fast-equals';
import { useSnackbar } from 'notistack';
import { ClosingCalendarFilters } from './custom-header/closing-calendar-filters';
import { Close, Download } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { intervalToDuration } from 'date-fns';
import * as ics from 'ics';
import { formatFileSafeDate } from '../../../helpers';

const title = 'Closing Calendar';

// Has to be outside of component or it imediately rerenders on move or resize
function formatFilters(date: Date, view: string, selectedEventType?: any[], searchTerm?: string) {
  let rangeType: any = '';
  switch (view) {
    case 'work_week':
      rangeType = 'week';
      break;
    case 'work_month':
    case 'month':
      rangeType = 'month';
      break;
    case 'day':
      rangeType = 'day';
      break;
  }

  moment.tz.setDefault('America/Chicago');
  const start = moment(date).startOf(rangeType).toDate();
  const end = moment(date).endOf(rangeType).toDate();
  return {
    startDate: start,
    endDate: end,
    eventTypes: selectedEventType.map(type => type.value),
    searchTerm: searchTerm
  };
}

export const ClosingCalendarPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [date, setDate] = useState<Date>(moment().toDate());
  const [myEvents, setMyEvents] = useState([]);
  const [myEventsInitialValues, setMyEventsInitialValues] = useState([]); // used for validation
  const [isSavingCalendar, setIsSavingCalendar] = useState<boolean>(false);
  const [isValid, setIsValid] = useState(deepEqual(myEvents, myEventsInitialValues));
  const isStartingState = () => deepEqual(myEvents, myEventsInitialValues);
  const [searchValue, setSearch] = useState<string>('');
  const [searchedValue, setSearchedValue] = useState<string>('');

  const [filters, setFilters] = useState<{
    search?: string;
    eventType?: any[];
  } | null>({});
  const [selectedEventTypes, setSelectedEventTypes] = useState<any[]>([]);

  const defaultView = Views.MONTH;
  const [view, setView] = useState(defaultView);
  const {
    isLoading,
    refetch: refetchEvents,
    isRefetching
  } = useQuery<IGetCalendarEvent[], Error>(
    ['closingEvents', date, filters, view],
    // @ts-ignore
    async () =>
      await getClosingCalendarEvents(formatFilters(date, view, selectedEventTypes, searchValue)),
    {
      notifyOnChangeProps: 'tracked',
      onSuccess: (d: IGetCalendarEvent[]) => {
        const events = d?.map(r => ({
          ...r,
          start: moment(r?.startDateTime).toDate(),
          end: moment(r?.endDateTime).toDate()
        }));
        setMyEvents(events);
        setMyEventsInitialValues(events);
      },
      onError: (e: any) => {
        enqueueSnackbar(e?.Detail || `Error loading calendar events.`, {
          variant: 'error'
        });
      }
    }
  );

  useEffect(() => {
    setIsValid(
      deepEqual(
        JSON.stringify(myEvents.sort((a, b) => a.buildId - b.buildId)),
        JSON.stringify(myEventsInitialValues.sort((a, b) => a.buildId - b.buildId))
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myEvents]);
  const [confirmModal, setConfirmModal] = useState(false);

  const getFriendlySnackbarSuccessMessage = (myEvents: IGetCalendarEvent[]) => {
    return myEvents.length === 1 ? 'Calendar Event Updated!' : 'Calendar Events Updated!';
  };
  const getFriendlySnackbarErrorMessage = (myEvents: IGetCalendarEvent[]) => {
    return myEvents.length === 1
      ? 'Calendar Event was not updated.'
      : 'Calendar Events were not updated.';
  };

  const onSubmit = async () => {
    try {
      setIsSavingCalendar(true);
      console.log('myEvents', myEvents);
      const data = myEvents.map(r => ({
        buildId: r.buildId,
        // send enum value
        eventType: r.eventTypeValue,
        closingAppointmentStart: new Date(r.start),
        closingAppointmentEnd: new Date(r.end)
      }));
      await updateClosingCalendarEvents(data);
      enqueueSnackbar(getFriendlySnackbarSuccessMessage(myEvents), {
        variant: 'success'
      });
    } catch (error) {
      enqueueSnackbar(getFriendlySnackbarErrorMessage(myEvents), {
        variant: 'error'
      });
    } finally {
      await refetchEvents();
      setIsSavingCalendar(false);
    }
  };

  const [isICSDownloading, setIsICSDownloading] = useState(false);

  const mapCalendarEventToICSEvent = (event: IGetCalendarEvent): ics.EventAttributes => {
    const duration = intervalToDuration({
      start: new Date(event.startDateTime),
      end: new Date(event.endDateTime)
    });

    const startDate = moment(event.startDateTime).toDate();
    const endDate = moment(event.endDateTime).toDate();
    return {
      start: event?.isAllDay
        ? [startDate.getFullYear(), startDate.getMonth() + 1, startDate.getDate()]
        : [
            startDate.getFullYear(),
            startDate.getMonth() + 1,
            startDate.getDate(),
            startDate.getHours(),
            startDate.getMinutes()
          ],
      ...(event?.isAllDay
        ? { end: [endDate.getFullYear(), endDate.getMonth() + 1, endDate.getDate()] }
        : { duration: { hours: duration.hours, minutes: duration.minutes } }),
      calName: 'Stone Martin Sales Calendar',
      title: event?.title,
      description: `${event?.eventType}${
        event?.neighborhoodName ? ` - ${event?.neighborhoodName}` : ''
      }`,
      location: event?.location ?? event?.address ?? '',
      organizer: { name: 'Stone Martin Sales', email: 'sales@stonemartin.com' }
    };
  };

  const handleICSEventsDownload = (events: IGetCalendarEvent[]) => {
    const icsEvents = events.map(mapCalendarEventToICSEvent);
    ics.createEvents(icsEvents, (error, value) => {
      if (error) {
        console.error(error);
        return;
      }
      const blob = new Blob([value], { type: 'text/calendar' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${formatFileSafeDate(date)}-${view.replace('_', '-')}-calendar.ics`;
      a.click();
      URL.revokeObjectURL(url);
    });
  };

  const handleICSEventDownload = (event: IGetCalendarEvent) => {
    // convert single event to ics event array for consistent mapping
    const icsEvents = [event].map(mapCalendarEventToICSEvent);
    ics.createEvents(icsEvents, (error, value) => {
      if (error) {
        console.error(error);
        return;
      }
      const blob = new Blob([value], { type: 'text/calendar' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${event.title}-${event.eventType ? `${event.eventType}-` : ''}calendar.ics`;
      a.click();
      URL.revokeObjectURL(url);
    });
  };
  return (
    <Page title={title}>
      <PageTitleWithButton
        pageTitleProps={{ title }}
        button={
          <Box flexWrap="wrap" display="flex" alignItems="center">
            <Box display="flex" gap={1}>
              <LoadingButton
                id={'download_ics'}
                color="primary"
                variant="contained"
                onClick={async () => {
                  setIsICSDownloading(true);
                  handleICSEventsDownload(myEvents);
                  setIsICSDownloading(false);
                }}
                disabled={isICSDownloading}
                loading={isICSDownloading}
                startIcon={<Download />}
              >
                Download Calendar
              </LoadingButton>

              <Button
                id="save-calendar-events"
                onClick={() => setConfirmModal(true)}
                color="primary"
                variant="contained"
                disabled={isValid || isStartingState() || isSavingCalendar}
              >
                {'Save'}
              </Button>
            </Box>
          </Box>
        }
      />
      <ClosingCalendarFilters
        isLoading={isLoading}
        applyFilters={(clearFilters?: boolean) => {
          if (clearFilters) {
            setFilters({
              eventType: [],
              search: ''
            });
            setSelectedEventTypes([]);
          } else {
            setFilters({
              ...filters,
              eventType: selectedEventTypes?.map(type => type.value) || undefined,
              search: searchValue
            });
          }
        }}
        selectedEventTypes={selectedEventTypes}
        setSelectedEventTypes={setSelectedEventTypes}
        isDisabled={isLoading}
        searchPlaceholder={'Search...'}
        handleSearch={(val: string) => {
          setFilters({
            ...filters,
            search: val
          });
        }}
        setSearchValue={setSearch}
        searchValue={searchValue}
      />
      {searchedValue && (
        <Box marginTop="1rem" display="flex" alignItems="center">
          <Typography color="secondary">Showing results for "{searchedValue}"</Typography>
          <Button
            sx={theme => ({
              '@media (min-width: 408px)': {
                marginTop: 3,
                marginLeft: theme.spacing(1)
              }
            })}
            id="clear-search"
            size="small"
            startIcon={<Close />}
            onClick={() => {
              setSearch('');
              setSearchedValue('');
            }}
          >
            Clear Search
          </Button>
        </Box>
      )}
      <ClosingCalendar
        myEvents={myEvents}
        setMyEvents={setMyEvents}
        isLoading={isLoading || isSavingCalendar}
        isRefetching={isRefetching}
        date={date}
        setDate={setDate}
        view={view}
        setView={setView}
        refetchEvents={refetchEvents}
        shouldWarn={!isValid}
        onSubmit={onSubmit}
        isConfirming={confirmModal}
        setIsConfirming={setConfirmModal}
        handleICSEventDownload={handleICSEventDownload}
      />
    </Page>
  );
};
